import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';

const I = (Icon: any) => Icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaTimes = I(FaIcons.FaTimes);
const FaArrowRight = I(FaIcons.FaArrowRight);
const FaCalendarAlt = I(FaIcons.FaCalendarAlt);
const FaMapMarkerAlt = I(FaIcons.FaMapMarkerAlt);

const navy = '#0a1f44';
const yellow = '#fbbf24';

const STORAGE_KEY = 'summerCampPopupDismissed_v1';
const SHOW_DELAY_MS = 2500;

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(10, 31, 68, 0.65);
  backdrop-filter: blur(6px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Card = styled(motion.div)`
  position: relative;
  background: linear-gradient(135deg, ${navy} 0%, #0f2c5e 100%);
  color: #fff;
  width: 100%;
  max-width: 460px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0,0,0,0.4);

  &::before {
    content: '';
    position: absolute;
    width: 240px;
    height: 240px;
    background: ${yellow};
    border-radius: 50%;
    opacity: 0.18;
    top: -120px;
    right: -80px;
    filter: blur(32px);
    pointer-events: none;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  z-index: 2;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.2); }
`;

const Body = styled.div`
  padding: 36px 32px 28px;
  position: relative;
  z-index: 1;

  @media (max-width: 480px) {
    padding: 32px 24px 24px;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(251,191,36,0.15);
  border: 1px solid rgba(251,191,36,0.4);
  color: ${yellow};
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 16px;

  .dot {
    width: 6px;
    height: 6px;
    background: ${yellow};
    border-radius: 50%;
    box-shadow: 0 0 8px ${yellow};
  }
`;

const Title = styled.h2`
  font-size: 1.7rem;
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.5px;
  margin: 0 0 10px;
  color: #fff;

  .accent { color: ${yellow}; }
`;

const Sub = styled.p`
  color: rgba(255,255,255,0.78);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 20px;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;

  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.88rem;
    color: rgba(255,255,255,0.9);

    svg { color: ${yellow}; flex-shrink: 0; }
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;

  .strike { color: rgba(255,255,255,0.45); text-decoration: line-through; font-size: 0.95rem; }
  .price { font-size: 2rem; font-weight: 900; color: #fff; letter-spacing: -1px; }
  .gst { color: rgba(255,255,255,0.65); font-size: 0.85rem; }
`;

const SaveBadge = styled.div`
  display: inline-block;
  background: ${yellow};
  color: ${navy};
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  margin-bottom: 22px;
`;

const CTAs = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 380px) {
    flex-direction: column;
  }
`;

const Primary = styled(Link)`
  flex: 1;
  background: ${yellow};
  color: ${navy};
  padding: 13px 20px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.95rem;
  text-decoration: none;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.15s;
  &:hover { transform: translateY(-1px); background: #fcd34d; }
`;

const Ghost = styled(Link)`
  background: transparent;
  color: #fff;
  padding: 13px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  text-align: center;
  border: 1px solid rgba(255,255,255,0.2);
  transition: background 0.15s;
  &:hover { background: rgba(255,255,255,0.05); }
`;

const SummerCampPopup: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) {
      // sessionStorage unavailable; fall through and show
    }
    if (dismissed) return;

    const id = window.setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  const close = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
    setOpen(false);
  };

  const price = 5000;

  return (
    <AnimatePresence>
      {open && (
        <Backdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={close}
        >
          <Card
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseBtn onClick={close} aria-label="Close">
              <FaTimes />
            </CloseBtn>

            <Body>
              <Eyebrow>
                <span className="dot" /> Registrations Open
              </Eyebrow>

              <Title>
                IIITD-IC <span className="accent">Summer Camp</span> 2026
              </Title>

              <Sub>
                10 days of hands-on robotics, IoT, AI, drones, and aero-modelling at IIIT Delhi.
                Take your kit home.
              </Sub>

              <Meta>
                <div className="row"><FaCalendarAlt /> 21 – 31 May 2026 · 9 AM – 12 PM</div>
                <div className="row"><FaMapMarkerAlt /> IIIT Delhi Campus, New Delhi</div>
              </Meta>

              <PriceRow>
                <span className="strike">₹11,999</span>
                <span className="price">₹{price.toLocaleString('en-IN')}</span>
                <span className="gst">+ GST</span>
              </PriceRow>
              <SaveBadge>
                Save ₹{(11999 - price).toLocaleString('en-IN')}
              </SaveBadge>

              <CTAs>
                <Primary to="/register" onClick={close}>
                  Register Now <FaArrowRight />
                </Primary>
                <Ghost to="/summer-camp" onClick={close}>
                  Learn More
                </Ghost>
              </CTAs>
            </Body>
          </Card>
        </Backdrop>
      )}
    </AnimatePresence>
  );
};

export default SummerCampPopup;
